/**
 * Extra pages for Ctrl+Shift+F "Find pages".
 *
 * Sidebar links live in DashboardLayout `menuItems`.
 * Dashboard routes that are NOT in the sidebar (or need extra keywords)
 * should be listed here so Find can discover them.
 *
 * When you add a new dashboard Route in App.js:
 * 1. Prefer adding it to `menuItems` (sidebar + Find), OR
 * 2. Add an entry below if it should stay out of the sidebar but still be Find-able.
 *
 * Path tokens are auto-indexed (e.g. /catalog-templates → "catalog templates"),
 * and catalog ↔ catalogue spelling is normalized in search.
 */
import React from "react";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import BackupIcon from "@mui/icons-material/Backup";
import LinkIcon from "@mui/icons-material/Link";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import DevicesIcon from "@mui/icons-material/Devices";
import StoreIcon from "@mui/icons-material/Store";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import WorkIcon from "@mui/icons-material/Work";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SchoolIcon from "@mui/icons-material/School";
import CouponIcon from "@mui/icons-material/LocalOffer";
import PeopleIcon from "@mui/icons-material/People";
import WalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import AdsIcon from "@mui/icons-material/Campaign";
import CodeIcon from "@mui/icons-material/Code";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import HistoryIcon from "@mui/icons-material/History";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import TuneIcon from "@mui/icons-material/Tune";
import InsightsIcon from "@mui/icons-material/Insights";

const icon = (Cmp) => React.createElement(Cmp);

/** Pages reachable in the app but not (only) via sidebar menuItems. */
export const EXTRA_FIND_PAGES = [
  {
    text: "App Trends",
    icon: icon(InsightsIcon),
    path: "/apptrends",
    roles: ["app_admin", "super_admin", "app_operator"],
    permission: "apptrends",
    description:
      "Day-wise and month-wise Loksy & Vyapar installations, registered users, and activity",
    keywords: [
      "apptrends",
      "app trends",
      "installations",
      "installs",
      "registered users",
      "day wise",
      "month wise",
      "statistics",
      "loksy",
      "vyapar",
    ],
  },
  {
    text: "Publish Ads",
    icon: icon(AdsIcon),
    path: "/locsy/ads",
    roles: ["franchisee_admin", "net_center_admin", "business_admin", "shop_admin"],
    description: "Create, publish, pay for and manage business advertisements.",
    keywords: ["ads", "advertisements", "publish ads", "create ads", "my ads", "promotions"],
  },
  {
    text: "Admin Control Screens",
    icon: icon(TuneIcon),
    path: "/admin-control-screens",
    roles: ["app_admin", "super_admin", "app_operator"],
    description:
      "One hub for Mitra/Citizen screens, approvals, wheel, boost, ads & platform controls",
    keywords: [
      "admin control screens",
      "admin controls",
      "screens controls",
      "wheel control",
      "approvals hub",
      "unified controls",
    ],
  },
  {
    text: "vyapar registration form",
    icon: icon(VerifiedUserIcon),
    path: "/business-admin/register",
    roles: ["app_admin", "super_admin", "app_operator"],
    permission: "vyapar_assisted_registration",
    description:
      "Register a Vyapar/Mitra merchant without OTP when self-registration OTP fails",
    keywords: [
      "vyapar registration form",
      "mitra register form",
      "mitra registration by admin",
      "mitra registration",
      "register without otp",
      "admin assisted mitra",
      "vyapar registration",
      "assisted registration",
      "register vyapar",
      "business-admin/register",
    ],
  },
  {
    text: "Catalog templates",
    icon: icon(Inventory2OutlinedIcon),
    path: "/catalog-templates",
    roles: ["app_admin", "super_admin"],
    description: "Smart Bill default product lists by shop type (Kirana, bakery, …)",
    keywords: [
      "catalogue",
      "catalog",
      "catalog templates",
      "catalogue templates",
      "smart bill defaults",
      "kirana template",
      "product templates",
      "hsn",
      "gst defaults",
    ],
  },
  {
    text: "Mitra Screens",
    icon: icon(DevicesIcon),
    path: "/mitrascreens",
    roles: ["app_admin", "super_admin"],
    description:
      "Edit Locsy Mitra hub cards — multilingual names, taglines, badges, current app thumbnails",
    keywords: [
      "mitra",
      "mitra screens",
      "mitrascreens",
      "/mitrascreens",
      "locsy mitra",
      "citizen interaction",
      "internal operations",
      "partner services",
      "feature cards",
      "tagline",
      "thumbnail",
      "quick action",
      "home",
      "dashboard cards mitra",
      "grow",
      "operate",
    ],
  },
  {
    text: "Town Pulse Screen",
    icon: icon(AdsIcon),
    path: "/content/townpulsescreen",
    roles: ["app_admin", "super_admin", "app_operator"],
    description:
      "Citizen Town Pulse reels — update image, pause, delete, views, demo/live feed toggles",
    keywords: [
      "town pulse",
      "townpulsescreen",
      "town pulse screen",
      "/content/townpulsescreen",
      "my town updates",
      "town status",
      "demo ads",
      "live ads",
      "pause",
      "delete",
      "views",
      "reels",
    ],
  },
  {
    text: "Loksy Home Screen Controls",
    icon: icon(DevicesIcon),
    path: "/loksyhomescreen_controls",
    roles: ["app_admin", "super_admin"],
    description:
      "Hide / display Citizen home categories — Govt Alerts, Real Estate, Food Zone & more",
    keywords: [
      "loksyhomescreen_controls",
      "loksy home screen controls",
      "citizen",
      "citizen screens",
      "citizenscreens",
      "/citizenscreens",
      "/loksyhomescreen_controls",
      "locsy citizen",
      "home carousel",
      "home categories",
      "govt alerts",
      "real estate",
      "movies events",
      "fresh market",
      "food zone",
      "learn zone",
      "carousel order",
      "hide section",
      "display section",
      "spotlight",
      "thumbnail",
      "merchandising",
      "home feed",
      "section order",
      "jump to position",
    ],
  },
  {
    text: "MyTown Screens",
    icon: icon(DevicesIcon),
    path: "/mytownscreens",
    roles: ["app_admin", "super_admin"],
    description:
      "Locsy Citizen MyTown category grid — card images, labels, hide & reorder",
    keywords: [
      "mytown",
      "my town",
      "mytownscreens",
      "/mytownscreens",
      "category cards",
      "kirana",
      "provision",
      "fancy shops",
      "stationery",
      "category order",
      "category thumbnails",
      "mytown merchandising",
    ],
  },
  {
    text: "Learn Zone Approvals",
    icon: icon(DevicesIcon),
    path: "/content/learn-zone-approvals",
    roles: ["app_admin", "super_admin", "app_operator"],
    description:
      "Approve Local Tutor (Mitra) Online & Campus courses for Citizen Learn Zone — KNLS unchanged",
    keywords: [
      "learn zone",
      "local tutors",
      "online courses",
      "campus classes",
      "lms marketplace",
      "course approval",
    ],
  },
  {
    text: "Online Course Sales",
    icon: icon(SchoolIcon),
    path: "/content/online-course-sales",
    roles: ["app_admin", "super_admin", "app_operator"],
    description:
      "Citizen / app online course purchases — KNLS sales totals; Mitra listed separately for settlement",
    keywords: [
      "online sales",
      "online course sales",
      "learn zone sales",
      "knls sales",
      "course purchases",
      "razorpay enroll",
      "student purchases",
      "lms sales",
      "third party courses",
      "mitra course sales",
    ],
  },
  {
    text: "Course Referrals",
    icon: icon(SchoolIcon),
    path: "/dashboard/students/course-referrals",
    roles: ["app_admin", "super_admin", "net_center_admin", "franchisee_admin"],
    description:
      "Vyapar peer course referrals — WhatsApp links, sales ledger, mark UPI settle status (Locsy does not pay)",
    keywords: [
      "course referrals",
      "peer referral",
      "whatsapp course",
      "referral commission",
      "promoter settle",
      "learn zone referral",
      "/dashboard/students/course-referrals",
    ],
  },
  {
    text: "Coupon Flow",
    icon: icon(CouponIcon),
    path: "/content/coupon-flow",
    roles: ["app_admin", "super_admin"],
    description:
      "DigiDrop — set coupons per popup / pamphlet / status engage + pool stats",
    keywords: [
      "coupon flow",
      "digidrop",
      "share with locsy",
      "ad rewards",
      "popup coupons",
      "pamphlet coupons",
      "/content/coupon-flow",
    ],
  },
  {
    text: "Citizen Coupon Management",
    icon: icon(CouponIcon),
    path: "/content/coupon-management",
    roles: ["app_admin", "super_admin"],
    description:
      "Distribution pool coupons & stats, ad types, worth bands, spinner, discovery",
    keywords: [
      "citizen coupon management",
      "coupon management",
      "distribution pool",
      "available coupons",
      "ad types",
      "worth",
      "spinner",
      "digidrop",
      "/content/coupon-management",
    ],
  },
  {
    text: "Boost Control",
    icon: icon(CouponIcon),
    path: "/content/boost-control",
    roles: ["app_admin", "super_admin"],
    description:
      "Locsy Boost — referral credits (coins/points), % rules, validity, checkout cap",
    keywords: [
      "boost control",
      "locsy boost",
      "coins",
      "points",
      "coinscontrol",
      "referral credits",
      "referral rewards",
      "/content/boost-control",
    ],
  },
  {
    text: "Coin Management",
    icon: icon(CouponIcon),
    path: "/content/coin-management",
    roles: [],
    description:
      "Manage coin earning, balances, recharge plans, spend rules, ledger and legal disclaimers",
    keywords: [
      "coin management",
      "coins",
      "wallet",
      "wallet management",
      "wallet balance",
      "add points",
      "add coins",
      "coin balance",
      "coin transactions",
      "coin ledger",
      "recharge plans",
      "earn rules",
      "spend rules",
      "coin analytics",
      "coin terms",
      "refund policy",
      "/wallet",
      "/content/coin-management",
    ],
  },
  {
    text: "Razorpay",
    icon: icon(AccountBalanceIcon),
    path: "/content/razorpay",
    roles: ["app_admin", "super_admin", "app_operator"],
    description:
      "Razorpay receipts, refunds, bank settlements — Mitra, Citizen, ads, wallet, LMS",
    keywords: [
      "razorpay",
      "razor pay",
      "razor-pay",
      "payments",
      "receipts",
      "settlement",
      "settled",
      "bank",
      "utr",
      "inflow",
      "outflow",
      "refunds",
      "/content/razorpay",
    ],
  },
  {
    text: "Membership Plans",
    icon: icon(VerifiedUserIcon),
    path: "/content/membership-plans",
    roles: ["app_admin", "super_admin"],
    description:
      "Free / Basic / Advance / Prime — prices, trial, volume caps, add-ons",
    keywords: [
      "membership plans",
      "membership",
      "loksy",
      "basic",
      "advance",
      "prime",
      "free listing",
      "saas membership",
      "plan pricing",
      "/content/membership-plans",
    ],
  },
  {
    text: "Subscription counts",
    icon: icon(VerifiedUserIcon),
    path: "/content/membership-subscribers",
    roles: ["app_admin", "super_admin"],
    description: "Aggregate Free / Trial / Basic / Advance / Prime counts — no PII",
    keywords: [
      "subscribers",
      "subscription counts",
      "trial ending",
      "/content/membership-subscribers",
    ],
  },
  {
    text: "Loksy Navigators Approval",
    icon: icon(VerifiedUserIcon),
    path: "/content/navigators",
    roles: ["app_admin", "super_admin", "app_operator"],
    description:
      "Approve / reject / suspend Navigator registrations; assign town & district",
    keywords: [
      "approvals",
      "navigator",
      "navigators",
      "navigator approval",
      "pending approval",
      "field force",
      "loksy navigator",
      "approve navigator",
      "/content/navigators",
    ],
  },
  {
    text: "Nav App Controls",
    icon: icon(TuneIcon),
    path: "/content/nav-app-controls",
    roles: ["app_admin", "super_admin"],
    description:
      "Navigator approval mandatory toggle, commissions ON/OFF, rates & UPI payouts",
    keywords: [
      "nav app controls",
      "navigator settings",
      "commission rates",
      "approval mandatory",
      "navigator commissions",
      "/content/nav-app-controls",
    ],
  },
  {
    text: "Shop polls",
    icon: icon(HowToVoteIcon),
    path: "/content/shop-polls",
    roles: ["app_admin", "super_admin"],
    description:
      "Mitra customer polls — votes, extra options, and shop statistics",
    keywords: [
      "shop polls",
      "polls",
      "poll statistics",
      "customer poll",
      "vote",
      "curry",
      "/content/shop-polls",
    ],
  },
  {
    text: "Locsy Spin",
    icon: icon(CouponIcon),
    path: "/content/lucky-wheel",
    roles: ["app_admin", "super_admin"],
    description:
      "Lucky wheel odds for listing publish, Mitra shop spin, and bill bonus",
    keywords: [
      "locsy spin",
      "lucky wheel",
      "spin",
      "shop spin",
      "probability",
      "/content/lucky-wheel",
    ],
  },
  {
    text: "Testers Feedback",
    icon: icon(FeedbackOutlinedIcon),
    path: "/testersfeedback",
    roles: ["app_admin", "super_admin"],
    description:
      "Review Mitra & Citizen tester feedback, edit Yes/No form, post updates to testers",
    keywords: [
      "testers feedback",
      "testersfeedback",
      "/testersfeedback",
      "tester feedback",
      "beta feedback",
      "bug report",
      "ui feedback",
      "logic bugs",
      "suggestions",
      "qa testing",
      "mitra feedback",
      "citizen feedback",
    ],
  },
  {
    text: "BetaGate",
    icon: icon(TuneIcon),
    path: "/betagate",
    roles: ["app_admin", "super_admin"],
    description:
      "Early access master switch, launch countdown, Mitra/Citizen allowlist & bill-user login toggle",
    keywords: [
      "betagate",
      "beta gate",
      "beta",
      "early access",
      "prelaunch",
      "pre-launch",
      "countdown",
      "launch date",
      "allowlist",
      "whitelist",
      "mitra access",
      "citizen access",
      "bill users",
      "smartbill login",
      "/betagate",
    ],
  },
  {
    text: "Profile",
    icon: icon(AccountCircleIcon),
    path: "/profile",
    roles: [
      "app_admin",
      "super_admin",
      "app_operator",
      "franchisee_admin",
      "net_center_admin",
      "franchisee_operator",
      "business_admin",
      "user",
    ],
    description: "Your account profile",
    keywords: ["account", "my profile", "settings profile"],
  },
  {
    text: "Active Sessions",
    icon: icon(DevicesIcon),
    path: "/profile/sessions",
    roles: [
      "app_admin",
      "super_admin",
      "app_operator",
      "franchisee_admin",
      "net_center_admin",
      "franchisee_operator",
      "business_admin",
      "user",
    ],
    description: "Devices and login sessions",
    keywords: ["sessions", "devices", "logged in devices"],
  },
  {
    text: "URL Manager",
    icon: icon(LinkIcon),
    path: "/url-manager",
    roles: ["app_admin", "super_admin"],
    description: "Manage and search admin URLs",
    keywords: ["urls", "links", "url manager"],
  },
  {
    text: "Backup & Export",
    icon: icon(BackupIcon),
    path: "/backup",
    roles: ["app_admin", "super_admin", "franchisee_admin", "net_center_admin"],
    description: "Backup and export data",
    keywords: ["backup", "export", "download data"],
  },
  {
    text: "Net Centers",
    icon: icon(BusinessCenterIcon),
    path: "/net-centers",
    roles: ["app_admin", "super_admin"],
    description: "Franchise / net center control",
    keywords: ["net centres", "net centers", "franchise network"],
  },
  {
    text: "Counter Sales Date-wise",
    icon: icon(AssessmentIcon),
    path: "/bills/counter-sales-datewise",
    roles: ["franchisee_admin", "net_center_admin", "app_admin", "super_admin"],
    description: "Date-wise counter sales tracker",
    keywords: ["counter sales", "datewise", "date wise sales"],
  },
  {
    text: "Post Bill Coupons",
    icon: icon(CouponIcon),
    path: "/bills/post-bill-coupons",
    roles: ["app_admin", "super_admin", "franchisee_admin", "net_center_admin"],
    description: "Coupons after billing",
    keywords: ["post bill coupons", "after bill coupons"],
  },
  {
    text: "Customer Dashboard",
    icon: icon(PeopleIcon),
    path: "/customer/dashboard",
    roles: [
      "franchisee_admin",
      "net_center_admin",
      "app_admin",
      "super_admin",
      "app_operator",
    ],
    description: "Customer dashboard view",
    keywords: ["customer dashboard", "customer history"],
  },
  {
    text: "Wallet Transactions",
    icon: icon(WalletIcon),
    path: "/content/coin-management",
    roles: [],
    description: "Loksy Wallet coin ledger and adjustments",
    keywords: ["wallet history", "credits history", "transactions", "add points"],
  },
  {
    text: "Business Admin Approval",
    icon: icon(VerifiedUserIcon),
    path: "/business-admin/approval",
    roles: ["app_admin", "super_admin", "app_operator"],
    permission: "business_admin_approval",
    description: "Approve business admins",
    keywords: ["business approval", "approve business admin"],
  },
  {
    text: "Job Alerts View",
    icon: icon(WorkIcon),
    path: "/job-alerts/view",
    roles: ["app_admin", "super_admin", "franchisee_admin", "net_center_admin"],
    description: "View job alerts",
    keywords: ["view jobs", "job alerts list"],
  },
  {
    text: "Job Alerts Approval",
    icon: icon(WorkIcon),
    path: "/job-alerts/approval",
    roles: ["app_admin", "super_admin"],
    description: "Approve job alerts",
    keywords: ["approve jobs", "job approval"],
  },
  {
    text: "Job Alerts Enquiries",
    icon: icon(WorkIcon),
    path: "/job-alerts/enquiries",
    roles: ["app_admin", "super_admin", "franchisee_admin", "net_center_admin"],
    description: "Job alert enquiries",
    keywords: ["job enquiries", "career enquiries"],
  },
  {
    text: "Training Programs",
    icon: icon(SchoolIcon),
    path: "/training-programs",
    roles: [
      "app_admin",
      "super_admin",
      "franchisee_admin",
      "net_center_admin",
      "user",
      "customer",
    ],
    description: "Public training programs listing",
    keywords: ["training programmes", "programs"],
  },
  {
    text: "Flashcards",
    icon: icon(SchoolIcon),
    path: "/dashboard/students/flashcards",
    roles: ["app_admin", "super_admin", "net_center_admin", "franchisee_admin"],
    description: "LMS flashcards",
    keywords: ["flash cards", "student flashcards"],
  },
  {
    text: "Assignment Review",
    icon: icon(FactCheckIcon),
    path: "/dashboard/students/assignments/review",
    roles: ["app_admin", "super_admin", "net_center_admin", "franchisee_admin"],
    description: "Review student assignments",
    keywords: ["assignment review", "mark assignments"],
  },
  {
    text: "Quiz Review",
    icon: icon(FactCheckIcon),
    path: "/dashboard/students/quizzes/review",
    roles: ["app_admin", "super_admin", "net_center_admin", "franchisee_admin"],
    description: "Review quiz marks, wrong answers, and daily exam pulse",
    keywords: ["quiz review", "quiz attempts", "wrong answers", "daily exams", "lms quiz"],
  },
  {
    text: "Student Attendance",
    icon: icon(AssessmentIcon),
    path: "/dashboard/students/attendance",
    roles: ["app_admin", "super_admin", "net_center_admin", "franchisee_admin"],
    description: "Student attendance",
    keywords: ["attendance", "student attendance"],
  },
  {
    text: "Coding Problems",
    icon: icon(CodeIcon),
    path: "/dashboard/students/coding/problems",
    roles: ["app_admin", "super_admin", "net_center_admin", "franchisee_admin"],
    description: "LMS coding problems",
    keywords: ["coding problems", "programming challenges"],
  },
  {
    text: "Coding Review",
    icon: icon(CodeIcon),
    path: "/dashboard/students/coding/review",
    roles: ["app_admin", "super_admin", "net_center_admin", "franchisee_admin"],
    description: "Review coding submissions",
    keywords: ["coding review", "code review"],
  },
  {
    text: "Old Track Sheets",
    icon: icon(HistoryIcon),
    path: "/dashboard/students/track-sheets/old",
    roles: ["app_admin", "super_admin", "net_center_admin", "franchisee_admin"],
    description: "Legacy track sheets",
    keywords: ["old track sheets", "legacy track"],
  },
  {
    text: "Citizen Requirements",
    icon: icon(StoreIcon),
    path: "/franchisee/citizen-requirements",
    roles: ["franchisee_admin", "net_center_admin"],
    description: "Citizen requirements inbox",
    keywords: ["citizen requirements", "requirements inbox"],
  },
  {
    text: "Franchisee Ads",
    icon: icon(AdsIcon),
    path: "/franchisee/ads",
    roles: ["franchisee_admin", "net_center_admin"],
    description: "Franchisee advertisements",
    keywords: ["franchise ads", "business ads"],
  },
  {
    text: "Franchisee Operators",
    icon: icon(PeopleIcon),
    path: "/franchisee/operators",
    roles: ["franchisee_admin", "net_center_admin"],
    description: "Manage franchisee operators",
    keywords: ["franchise operators", "operators"],
  },
];

/** Turn /catalog-templates into searchable words. */
export function pathToSearchTokens(path = "") {
  return String(path)
    .replace(/^\//, "")
    .split(/[/?&#=._-]+/)
    .filter(Boolean)
    .join(" ");
}

/** Normalize US/UK and common alternate spellings for Find. */
export function withSpellingAliases(text = "") {
  const lower = String(text).toLowerCase();
  const extras = [];
  if (/\bcatalog(ue)?s?\b/.test(lower) || lower.includes("catalog")) {
    extras.push("catalog", "catalogue", "catalogs", "catalogues");
  }
  if (/\bcentres?\b/.test(lower)) extras.push("center", "centers");
  if (/\bcenters?\b/.test(lower)) extras.push("centre", "centres");
  if (/\bprogrammes?\b/.test(lower)) extras.push("program", "programs");
  if (/\bprograms?\b/.test(lower)) extras.push("programme", "programmes");
  return extras.length ? `${text} ${extras.join(" ")}` : text;
}

export function buildPageSearchHaystack(item) {
  const base = [
    item.text,
    item.description,
    item.path,
    pathToSearchTokens(item.path),
    ...(item.keywords || []),
  ]
    .filter(Boolean)
    .join(" ");
  return withSpellingAliases(base).toLowerCase();
}

/**
 * Merge sidebar menu items with EXTRA_FIND_PAGES (by path),
 * keeping menu icons/text when both exist and unioning keywords.
 */
export function mergeFindPageLists(menuItems = [], extraPages = EXTRA_FIND_PAGES) {
  const byPath = new Map();

  menuItems.forEach((item) => {
    const key = item.path;
    const existing = byPath.get(key);
    if (!existing) {
      byPath.set(key, { ...item });
      return;
    }
    byPath.set(key, {
      ...existing,
      ...item,
      keywords: [...new Set([...(existing.keywords || []), ...(item.keywords || [])])],
      description: item.description || existing.description,
    });
  });

  extraPages.forEach((item) => {
    const key = item.path;
    const existing = byPath.get(key);
    if (!existing) {
      byPath.set(key, { ...item, id: item.id || `extra:${item.path}` });
      return;
    }
    byPath.set(key, {
      ...existing,
      keywords: [...new Set([...(existing.keywords || []), ...(item.keywords || [])])],
      description: existing.description || item.description,
      text: existing.text || item.text,
      icon: existing.icon || item.icon,
    });
  });

  return Array.from(byPath.values());
}
